import { BufferAttribute, BufferGeometry } from "three";

// Real-world blade proportions (metres) — see .extras/design/HANDOFF-katana-3d-build.md §1.
// Nagasa (blade length) 65-70cm, tapering from ~3cm width at the base.
export const BLADE_LENGTH = 0.68;
export const BLADE_BASE_WIDTH = 0.03; // mune-to-ha span at the base (the "s" dimension)

// Cross-section proportions, as fractions of the current width/thickness taper.
const MUNE_HALF_FRAC = 0.05; // flat spine half-width
const HI_INSET_FRAC = 0.62; // groove inset point, fraction of the way from mune to ridge
const HI_INSET_PULL = 0.72; // how far the groove point is pulled inward (< ridge half-width)
const RIDGE_HALF_FRAC = 0.16; // shinogi ridge half-width (blade thickness at the ridge)
const RIDGE_Y_FRAC = 0.62; // ridge sits closer to the mune than to the ha, per shinogi-zukuri

const YOKOTE_T = 0.86; // fraction of length where the kissaki taper begins
const MAIN_SEGMENTS = 11; // rings from base to yokote
const KISSAKI_SEGMENTS = 4; // rings from yokote to tip
const TIP_TAPER = 0.035; // fraction of base width remaining at the very tip (nonzero — avoids degenerate triangles)

// Blade curvature (torii-zori style: uniform curve, tangent to the grip axis
// at the base so the blade leaves the habaki with no kink, sweeping the
// kissaki up toward the mune side by the time it reaches the tip). The ha
// (edge) sits on -Y (see crossSectionPoints), so curving toward +Y keeps the
// edge on the convex outer side of the sori — correct for a real katana.
export const SORI_RISE = 0.055; // tip rise above the grip axis (~1.4cm sori depth for a 68cm nagasa)

// Cross-section point count — see the shinogi-zukuri profile built in
// crossSectionPoints(). 10, not 7: the ridge (shinogi) and edge (ha) points
// are each duplicated so the faces meeting there get their own vertex
// normals instead of being smoothed together by computeVertexNormals() —
// that's what makes the shinogi ridge and the ha read as crisp facets with
// a sharp travelling specular highlight instead of a rounded bar.
const PROFILE_POINTS = 10;

// Which consecutive profile-point pairs form an actual swept face. The
// ridge/edge duplicates (2↔3, 4↔5, 6↔7) sit at the same (x,y) and are never
// connected to each other — only to their respective neighbour — so no
// degenerate zero-area face is ever built between a duplicate pair.
const FACE_PAIRS: [number, number][] = [
  [0, 1],
  [1, 2],
  [3, 4],
  [5, 6],
  [7, 8],
  [8, 9],
  [9, 0],
];

export function bladeWidthScaleAt(t: number): number {
  if (t <= YOKOTE_T) return 1.0 - (t / YOKOTE_T) * 0.25;
  const kt = (t - YOKOTE_T) / (1 - YOKOTE_T);
  return 0.75 - kt * (0.75 - TIP_TAPER);
}

export function bladeLocalZAt(t: number): number {
  return -t * BLADE_LENGTH;
}

export function bladeEdgeYAt(t: number): number {
  return -(BLADE_BASE_WIDTH * bladeWidthScaleAt(t)) * 0.5 + SORI_RISE * t * t;
}

export function bladeRidgeYAt(t: number): number {
  const width = BLADE_BASE_WIDTH * bladeWidthScaleAt(t);
  return bladeEdgeYAt(t) + width * RIDGE_Y_FRAC;
}

/** One asymmetric shinogi-zukuri cross-section ring, in local (x, y), for a given half-span `s`. */
function crossSectionPoints(s: number, out: Float32Array, offset: number) {
  const muneY = s * 0.5;
  const edgeY = -s * 0.5;
  // Ridge sits RIDGE_Y_FRAC of the way from the edge toward the mune —
  // closer to the spine than to the ha, per shinogi-zukuri proportions.
  const ridgeY = edgeY + (muneY - edgeY) * RIDGE_Y_FRAC;
  const muneHalf = s * MUNE_HALF_FRAC;
  const ridgeHalf = s * RIDGE_HALF_FRAC;
  const hiY = ridgeY + (muneY - ridgeY) * HI_INSET_FRAC;
  const hiHalf = ridgeHalf * HI_INSET_PULL;

  // Order traces: mune-right -> hi-right -> ridge-right(x2) -> edge tip(x2)
  // -> ridge-left(x2) -> hi-left -> mune-left -> (closes back to mune-right).
  // A simple, non-self-intersecting decagon with duplicated ridge/edge
  // vertices — asymmetric top(mune)-to-bottom(ha), symmetric left-right.
  const pts: [number, number][] = [
    [muneHalf, muneY],
    [hiHalf, hiY],
    [ridgeHalf, ridgeY],
    [ridgeHalf, ridgeY],
    [0, edgeY],
    [0, edgeY],
    [-ridgeHalf, ridgeY],
    [-ridgeHalf, ridgeY],
    [-hiHalf, hiY],
    [-muneHalf, muneY],
  ];
  for (let i = 0; i < PROFILE_POINTS; i++) {
    out[offset + i * 2] = pts[i][0];
    out[offset + i * 2 + 1] = pts[i][1];
  }
}

/**
 * Builds the katana blade as a swept asymmetric shinogi-zukuri cross-section
 * along a curved (sori) spine, tapering to a kissaki tip. A hamon "temper
 * band" is baked in as vertex colors near the edge (multiplied against the
 * material's base color) rather than a second mesh or a texture — cheap,
 * and per the handoff doc, understated is correct (nobody reads fine detail
 * on a fast-moving blade).
 *
 * The mesh's own local origin is the base (habaki line, Z=0); the kissaki
 * apex sits at Z = -BLADE_LENGTH. Callers position the mesh in their own
 * local space and derive the tip offset from the exported BLADE_LENGTH.
 */
export function createKatanaBladeGeometry(): BufferGeometry {
  const totalSegments = MAIN_SEGMENTS + KISSAKI_SEGMENTS;
  const ringCount = totalSegments + 1;

  const positions = new Float32Array(ringCount * PROFILE_POINTS * 3);
  const colors = new Float32Array(ringCount * PROFILE_POINTS * 3);
  const uvs = new Float32Array(ringCount * PROFILE_POINTS * 2);
  const profile2D = new Float32Array(PROFILE_POINTS * 2);

  // Per-profile-point U coordinate: normalized height in the cross-section,
  // 0 at the ha (edge) and 1 at the mune (spine). Left and right faces share
  // the same U (mirrored) — the hamon/roughness texture is symmetric across
  // both faces (as on a real blade), which also means no UV seam anywhere.
  // These fractions are scale-invariant, so they're constant along the sweep.
  const ridgeU = RIDGE_Y_FRAC;
  const hiU = ridgeU + (1 - ridgeU) * HI_INSET_FRAC;
  const profileU = [1, hiU, ridgeU, ridgeU, 0, 0, ridgeU, ridgeU, hiU, 1];

  for (let r = 0; r < ringCount; r++) {
    const t = r / totalSegments;

    // Two-stage taper: gentle along the body, steeper past the yokote —
    // the rate change is what reads as the yokote crease separating the
    // kissaki from the body.
    const widthScale = bladeWidthScaleAt(t);

    const s = BLADE_BASE_WIDTH * widthScale;
    crossSectionPoints(s, profile2D, 0);

    // Sori: quadratic bow, zero slope at t=0 so it meets the habaki/tsuba
    // tangent to the grip axis (no kink), rising toward the mune side as it
    // sweeps to the kissaki.
    const sori = SORI_RISE * t * t;
    const z = bladeLocalZAt(t);

    // Hamon band: brighter tint near the edge (last profile point pairs
    // closest to the ha), with a slow wavy modulation along the length so
    // it reads as an organic temper line, not a hard stripe.
    const wave = 0.5 + 0.5 * Math.sin(t * 26 + 1.7);

    // Boshi: the temper brightening wrapping the kissaki — the whole tip
    // section past the yokote lifts slightly toward white, so the point
    // carries the reference plate's "boshi highlight on the tip."
    const boshi = t > YOKOTE_T ? ((t - YOKOTE_T) / (1 - YOKOTE_T)) * 0.12 : 0;

    for (let p = 0; p < PROFILE_POINTS; p++) {
      const idx = r * PROFILE_POINTS + p;
      positions[idx * 3 + 0] = profile2D[p * 2 + 0];
      positions[idx * 3 + 1] = profile2D[p * 2 + 1] + sori;
      positions[idx * 3 + 2] = z;

      uvs[idx * 2 + 0] = profileU[p];
      uvs[idx * 2 + 1] = t;

      // Points 2-7 (ridge-right x2, edge tip x2, ridge-left x2) are nearest
      // the ha; tint them toward full brightness, modulated by the hamon
      // wave. Everything else (mune, hi-groove) stays at the dimmer base tint.
      const nearEdge =
        p >= 2 && p <= 7 ? (p === 4 || p === 5 ? 1.0 : 0.6) : 0.0;
      const tint = Math.min(1, 0.8 + nearEdge * 0.2 * wave + boshi);
      colors[idx * 3 + 0] = tint;
      colors[idx * 3 + 1] = tint;
      colors[idx * 3 + 2] = Math.min(1, tint * 1.02);
    }
  }

  // Triangulate the tube: for each segment, connect ring r to ring r+1
  // across each real face of the profile (FACE_PAIRS — skips the
  // zero-width gaps between duplicated ridge/edge vertices).
  const indices: number[] = [];
  for (let r = 0; r < totalSegments; r++) {
    for (const [p, pNext] of FACE_PAIRS) {
      const a = r * PROFILE_POINTS + p;
      const b = r * PROFILE_POINTS + pNext;
      const c = (r + 1) * PROFILE_POINTS + p;
      const d = (r + 1) * PROFILE_POINTS + pNext;
      indices.push(a, b, d, a, d, c);
    }
  }

  // End caps (base at ring 0, tip at the last ring) — simple fans, closing
  // off both ends so there's no visible hollow even at grazing angles.
  const vertCount = positions.length / 3;
  const baseCenterIdx = vertCount;
  const tipCenterIdx = vertCount + 1;
  const lastRing = totalSegments;

  const cappedPositions = new Float32Array((vertCount + 2) * 3);
  cappedPositions.set(positions);
  cappedPositions[baseCenterIdx * 3 + 0] = 0;
  cappedPositions[baseCenterIdx * 3 + 1] = 0;
  cappedPositions[baseCenterIdx * 3 + 2] = 0;
  cappedPositions[tipCenterIdx * 3 + 0] = 0;
  cappedPositions[tipCenterIdx * 3 + 1] = SORI_RISE; // sori(t=1) = SORI_RISE
  cappedPositions[tipCenterIdx * 3 + 2] = -BLADE_LENGTH;

  const cappedColors = new Float32Array((vertCount + 2) * 3);
  cappedColors.set(colors);
  for (const c of [baseCenterIdx, tipCenterIdx]) {
    cappedColors[c * 3 + 0] = 0.8;
    cappedColors[c * 3 + 1] = 0.8;
    cappedColors[c * 3 + 2] = 0.82;
  }

  // Cap centres sit mid-profile in UV space; the base cap is hidden inside
  // the habaki and the tip cap is sub-millimetre, so the exact value is
  // cosmetic — it just has to exist for the attribute counts to match.
  const cappedUvs = new Float32Array((vertCount + 2) * 2);
  cappedUvs.set(uvs);
  cappedUvs[baseCenterIdx * 2 + 0] = 0.5;
  cappedUvs[baseCenterIdx * 2 + 1] = 0;
  cappedUvs[tipCenterIdx * 2 + 0] = 0.5;
  cappedUvs[tipCenterIdx * 2 + 1] = 1;

  for (const [p, pNext] of FACE_PAIRS) {
    indices.push(baseCenterIdx, pNext, p);
    const tipP = lastRing * PROFILE_POINTS + p;
    const tipPNext = lastRing * PROFILE_POINTS + pNext;
    indices.push(tipCenterIdx, tipP, tipPNext);
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new BufferAttribute(cappedPositions, 3));
  geometry.setAttribute("color", new BufferAttribute(cappedColors, 3));
  geometry.setAttribute("uv", new BufferAttribute(cappedUvs, 2));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();

  return geometry;
}
